<template>
  <div class="role-management app-page app-page--fill">
    <el-card class="main-card">
      <div class="toolbar">
        <div class="toolbar-left">
          <span class="page-title">{{ pageTitle }}</span>
        </div>
        <div class="toolbar-right">
          <el-input
            v-model="keyword"
            placeholder="关键字搜索：名称/说明/创建人/修改人"
            clearable
            style="width: 320px"
            @keyup.enter="handleKeywordSearch"
            @clear="handleKeywordSearch"
          />
          <el-button @click="handleKeywordSearch">搜索</el-button>
          <el-button v-if="can('新增')" type="primary" :icon="Plus" @click="handleCreate">新增角色</el-button>
          <el-button
            type="primary"
            circle
            size="small"
            class="app-refresh-btn"
            :icon="Refresh"
            :loading="loading"
            @click="loadRoleList"
            title="刷新"
          />
        </div>
      </div>

      <el-table
        :data="pagedRoleList"
        border
        stripe
        v-loading="loading"
        style="width: 100%"
        :max-height="tableMaxHeight"
      >
        <el-table-column type="index" label="序号" width="70" align="center" :index="getTableIndex" />
        <el-table-column prop="Name" label="名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="Description" label="说明" min-width="220" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.Description || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="AuthorityScope" label="数据权限" min-width="180">
          <template #default="{ row }">
            <el-tag :type="getAuthorityScopeTagType(row.AuthorityScope)">
              {{ getAuthorityScopeLabel(row.AuthorityScope) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="Enabled" label="状态" width="90" align="center">
          <template #default="{ row }">
            <el-tag :type="row.Enabled ? 'success' : 'danger'">{{ row.Enabled ? '启用' : '停用' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="OrderSort" label="排序" width="90" align="center" />
        <el-table-column prop="CreateBy" label="创建人" width="130" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.CreateBy || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="CreateTime" label="创建时间" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">
            {{ formatTime(row.CreateTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="ModifyBy" label="修改人" width="130" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.ModifyBy || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="ModifyTime" label="修改时间" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">
            {{ formatTime(row.ModifyTime) }}
          </template>
        </el-table-column>
        <el-table-column v-if="canAny('编辑', '删除')" label="操作" width="160" fixed="right" align="center">
          <template #default="{ row }">
            <el-button v-if="can('编辑')" type="primary" :icon="Edit" link size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button v-if="can('删除')" type="danger" :icon="Delete" link size="small" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrapper" v-if="filteredRoleList.length > 0">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="filteredRoleList.length"
          layout="total, sizes, prev, pager, next, jumper"
          @current-change="handlePageChange"
          @size-change="handlePageSizeChange"
        />
      </div>
    </el-card>

    <el-dialog
      v-model="dialogVisible"
      :title="dialogMode === 'create' ? '新增角色' : '编辑角色'"
      width="860px"
      top="0"
      :close-on-click-modal="false"
      :lock-scroll="true"
      modal-class="role-edit-modal"
      class="role-edit-dialog"
      destroy-on-close
    >
      <div class="role-form-panel">
        <div class="section-title">角色信息</div>
        <el-form ref="formRef" :model="formData" :rules="formRules" label-width="100px">
          <el-row :gutter="16">
            <el-col :xs="24" :md="12">
              <el-form-item label="名称" prop="Name">
                <el-input v-model="formData.Name" placeholder="请输入角色名称" maxlength="50" />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12">
              <el-form-item label="排序" prop="OrderSort">
                <el-input-number v-model="formData.OrderSort" :min="0" :max="9999" controls-position="right" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12">
              <el-form-item label="数据权限" prop="AuthorityScope">
                <el-select v-model="formData.AuthorityScope" placeholder="请选择数据权限" style="width: 100%">
                  <el-option v-for="item in authorityScopeOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12">
              <el-form-item label="状态" prop="Enabled">
                <el-switch v-model="formData.Enabled" active-text="启用" inactive-text="停用" />
              </el-form-item>
            </el-col>
            <el-col :xs="24">
              <el-form-item label="说明" prop="Description">
                <el-input v-model="formData.Description" type="textarea" :rows="3" placeholder="请输入说明" maxlength="200" show-word-limit />
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </div>

      <div v-if="isCustomAuthorityScope" class="department-tree-panel" v-loading="departmentTreeLoading">
        <div class="department-tree-header">
          <span class="department-tree-title">自定义数据权限（部门）</span>
          <div class="department-tree-actions">
            <el-input
              v-model="departmentSearchKeyword"
              placeholder="搜索部门并定位"
              clearable
              size="small"
              style="width: 220px"
              @keyup.enter="handleDepartmentSearch"
            />
            <el-button size="small" @click="handleDepartmentSearch">搜索</el-button>
            <el-button size="small" @click="setAllDepartmentChecked">全选</el-button>
            <el-button size="small" @click="clearDepartmentChecked">清空</el-button>
            <el-button
              type="primary"
              circle
              size="small"
              class="app-refresh-btn"
              :icon="Refresh"
              :loading="departmentTreeLoading"
              @click="loadDepartmentTree"
              title="刷新部门"
            />
          </div>
        </div>
        <el-scrollbar ref="departmentScrollbarRef" class="department-tree-scroll" :style="{ height: departmentTreeScrollHeight }">
          <el-tree
            ref="departmentTreeRef"
            :data="departmentTree"
            :props="departmentTreeProps"
            node-key="value"
            highlight-current
            show-checkbox
            default-expand-all
            :expand-on-click-node="false"
            @check="handleDepartmentTreeCheck"
          />
          <el-empty v-if="!departmentTreeLoading && departmentTree.length === 0" description="暂无部门树数据" />
        </el-scrollbar>
      </div>

      <div class="permission-tree-panel" :class="{ 'with-department': isCustomAuthorityScope }" v-loading="treeLoading">
        <div class="permission-tree-header">
          <span class="permission-tree-title">菜单权限</span>
          <div class="permission-tree-actions">
            <el-input
              v-model="permissionSearchKeyword"
              placeholder="搜索菜单并定位"
              clearable
              size="small"
              style="width: 220px"
              @keyup.enter="handlePermissionSearch"
            />
            <el-button size="small" @click="handlePermissionSearch">搜索</el-button>
            <el-button size="small" @click="setAllPermissionChecked">全选</el-button>
            <el-button size="small" @click="clearPermissionChecked">清空</el-button>
            <el-button
              type="primary"
              circle
              size="small"
              class="app-refresh-btn"
              :icon="Refresh"
              :loading="treeLoading"
              @click="loadPermissionTree"
              title="刷新菜单"
            />
          </div>
        </div>

        <el-scrollbar ref="permissionScrollbarRef" class="permission-tree-scroll" :style="{ height: permissionTreeScrollHeight }">
          <el-tree
            ref="permissionTreeRef"
            :data="permissionTree"
            :props="permissionTreeProps"
            node-key="value"
            highlight-current
            show-checkbox
            default-expand-all
            :expand-on-click-node="false"
            @check="handleTreeCheck"
          >
            <template #default="{ data }">
              <div class="tree-node-content">
                <span class="tree-node-label">{{ data.label }}</span>
                <div v-if="data.btns && data.btns.length > 0" class="tree-node-btns" @click.stop>
                  <el-checkbox
                    v-for="btn in data.btns"
                    :key="btn.value"
                    :model-value="isBtnChecked(btn.value)"
                    :disabled="btn.disabled"
                    @change="(checked: boolean) => handleBtnCheckChange(data, btn, checked)"
                    @click.stop
                  >
                    {{ btn.label }}
                  </el-checkbox>
                </div>
              </div>
            </template>
          </el-tree>
          <el-empty v-if="!treeLoading && permissionTree.length === 0" description="暂无菜单权限数据" />
        </el-scrollbar>
      </div>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button v-if="can('保存')" type="primary" :loading="saving" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, nextTick, onUnmounted, computed, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { FormInstance } from 'element-plus'
import type { ElTree } from 'element-plus'
import { Plus, Refresh, Edit, Delete } from '@element-plus/icons-vue'
import { PermissionManagement } from '@/network'
import { useRouteTitle } from '@/utils/useRouteTitle'
import refreshCurrentUserMenus from '@/utils/refreshCurrentUserMenus'
import { useRouter } from 'vue-router'
import { useButtonPermission } from '@/utils/buttonPermission'

interface RoleItem {
  IsDeleted: boolean
  Name: string
  Description: string | null
  OrderSort: number
  Dids: string | null
  AuthorityScope: number
  Enabled: boolean
  CreateId: string | null
  CreateBy: string | null
  CreateTime: string | null
  ModifyId: string | null
  ModifyBy: string | null
  ModifyTime: string | null
  Id: string
}

interface PermissionNode {
  label: string
  value: string
  disabled?: boolean
  btns?: PermissionBtn[]
  children?: PermissionNode[]
}

interface PermissionBtn {
  value: string
  label: string
  disabled?: boolean
}

interface DepartmentNode {
  label: string
  value: string
  disabled?: boolean
  children?: DepartmentNode[]
}

interface RoleFormData {
  IsDeleted: boolean
  Name: string
  Description: string
  OrderSort: number
  Dids: string
  AuthorityScope: number
  Enabled: boolean
  CreateId: string | null
  CreateBy: string | null
  CreateTime: string | null
  ModifyId: string | null
  ModifyBy: string | null
  ModifyTime: string | null
  Id: string | null
}

const loading = ref(false)
const router = useRouter()
const pageTitle = useRouteTitle('角色管理')
const { can, canAny } = useButtonPermission('/QuanXianGuanLi/juese.vue')
const saving = ref(false)
const treeLoading = ref(false)
const departmentTreeLoading = ref(false)
const dialogVisible = ref(false)
const dialogMode = ref<'create' | 'edit'>('create')
const roleList = ref<RoleItem[]>([])
const permissionTree = ref<PermissionNode[]>([])
const departmentTree = ref<DepartmentNode[]>([])
const checkedPermissionIds = ref<string[]>([])
const selectedDepartmentIds = ref<string[]>([])
const selectedBtnIds = ref<string[]>([])
const departmentSearchKeyword = ref('')
const permissionSearchKeyword = ref('')
const departmentSearchState = reactive({
  keyword: '',
  index: -1
})
const permissionSearchState = reactive({
  keyword: '',
  index: -1
})
const keyword = ref('')
const activeKeyword = ref('')
const formRef = ref<FormInstance>()
const permissionTreeRef = ref<InstanceType<typeof ElTree>>()
const departmentTreeRef = ref<InstanceType<typeof ElTree>>()
const permissionScrollbarRef = ref<any>(null)
const departmentScrollbarRef = ref<any>(null)
const tableMaxHeight = ref(500)
const bodyOverflowBackup = ref('')
const htmlOverflowBackup = ref('')
const pagination = reactive({
  page: 1,
  pageSize: 20
})

const permissionTreeProps = {
  children: 'children',
  label: 'label'
}

const departmentTreeProps = {
  children: 'children',
  label: 'label'
}

const authorityScopeOptions = [
  { value: -1, label: '无任何数据权限' },
  { value: 1, label: '自定义数据权限' },
  { value: 2, label: '本部门数据权限' },
  { value: 3, label: '本部门及下级所有部门权限' },
  { value: 4, label: '仅自己数据权限' },
  { value: 5, label: '全部数据权限' }
]

const authorityScopeMap: Record<number, string> = {
  [-1]: '无任何数据权限',
  1: '自定义数据权限',
  2: '本部门数据权限',
  3: '本部门及下级所有部门权限',
  4: '仅自己数据权限',
  5: '全部数据权限'
}

const formData = reactive<RoleFormData>({
  IsDeleted: false,
  Name: '',
  Description: '',
  OrderSort: 1,
  Dids: '',
  AuthorityScope: -1,
  Enabled: true,
  CreateId: null,
  CreateBy: null,
  CreateTime: null,
  ModifyId: null,
  ModifyBy: null,
  ModifyTime: null,
  Id: null
})

const formRules = {
  Name: [{ required: true, message: '请输入角色名称', trigger: 'blur' }],
  AuthorityScope: [{ required: true, message: '请选择数据权限', trigger: 'change' }],
  OrderSort: [{ required: true, message: '请输入排序值', trigger: 'change' }]
}

const calculateTableHeight = () => {
  nextTick(() => {
    tableMaxHeight.value = window.innerHeight - 300
  })
}

const getAuthorityScopeLabel = (value: number) => {
  return authorityScopeMap[value] || `未知权限(${value})`
}

const getAuthorityScopeTagType = (value: number) => {
  if (value === -1) return 'info'
  if (value === 4) return 'warning'
  if (value === 5) return 'success'
  return 'primary'
}

const formatTime = (value: string | null) => {
  if (!value || value === '0001-01-01 00:00:00') {
    return '-'
  }
  return value
}

const normalizeRoleList = (data: any): RoleItem[] => {
  if (!Array.isArray(data)) return []
  return data.map((item: any) => ({
    IsDeleted: Boolean(item?.IsDeleted),
    Name: item?.Name || '',
    Description: item?.Description ?? null,
    OrderSort: Number(item?.OrderSort ?? 0),
    Dids: item?.Dids ?? null,
    AuthorityScope: Number(item?.AuthorityScope ?? -1),
    Enabled: Boolean(item?.Enabled),
    CreateId: item?.CreateId != null ? String(item.CreateId) : null,
    CreateBy: item?.CreateBy ?? null,
    CreateTime: item?.CreateTime ?? null,
    ModifyId: item?.ModifyId != null ? String(item.ModifyId) : null,
    ModifyBy: item?.ModifyBy ?? null,
    ModifyTime: item?.ModifyTime ?? null,
    Id: item?.Id != null ? String(item.Id) : ''
  }))
}

const normalizePermissionTree = (nodes: any[]): PermissionNode[] => {
  return nodes.map((node: any) => {
    const rawChildren = node?.children ?? node?.Children ?? []
    const rawBtns = node?.btns ?? node?.Btns ?? []
    const label = String(node?.label ?? node?.Label ?? node?.name ?? node?.Name ?? node?.title ?? node?.Title ?? '-')
    const value = String(node?.value ?? node?.Value ?? node?.id ?? node?.Id ?? '')
    return {
      label,
      value,
      disabled: Boolean(node?.disabled),
      btns: Array.isArray(rawBtns)
        ? rawBtns.map((btn: any) => ({
            value: String(btn?.value ?? btn?.Value ?? btn?.id ?? btn?.Id ?? ''),
            label: String(btn?.label ?? btn?.Label ?? btn?.name ?? btn?.Name ?? '-'),
            disabled: Boolean(btn?.disabled)
          }))
        : [],
      children: Array.isArray(rawChildren) && rawChildren.length > 0 ? normalizePermissionTree(rawChildren) : undefined
    }
  })
}

const normalizeDepartmentTree = (nodes: any[]): DepartmentNode[] => {
  return nodes.map((node: any) => {
    const rawChildren = node?.children ?? node?.Children ?? []
    const label = String(node?.label ?? node?.Label ?? node?.name ?? node?.Name ?? '-')
    const value = String(node?.value ?? node?.Value ?? node?.id ?? node?.Id ?? '')
    return {
      label,
      value,
      disabled: Boolean(node?.disabled),
      children: Array.isArray(rawChildren) && rawChildren.length > 0 ? normalizeDepartmentTree(rawChildren) : undefined
    }
  })
}

const extractIdFromItem = (item: any): string | null => {
  if (item === null || item === undefined) return null
  if (typeof item === 'string' || typeof item === 'number') {
    const text = String(item).trim()
    return text ? text : null
  }
  const rawValue = item?.value ?? item?.Value ?? item?.id ?? item?.Id ?? item?.pid ?? item?.Pid ?? item?.PermissionId
  if (rawValue === null || rawValue === undefined) return null
  const text = String(rawValue).trim()
  return text ? text : null
}

const filteredRoleList = computed(() => {
  const searchText = activeKeyword.value.trim().toLowerCase()
  if (!searchText) {
    return roleList.value
  }
  return roleList.value.filter(item => {
    const fields = [item.Name, item.Description, item.CreateBy, item.ModifyBy]
    return fields.some(field => String(field || '').toLowerCase().includes(searchText))
  })
})

const pagedRoleList = computed(() => {
  const start = (pagination.page - 1) * pagination.pageSize
  const end = start + pagination.pageSize
  return filteredRoleList.value.slice(start, end)
})

const isCustomAuthorityScope = computed(() => Number(formData.AuthorityScope) === 1)
const permissionTreeScrollHeight = computed(() => (isCustomAuthorityScope.value ? '170px' : '320px'))
const departmentTreeScrollHeight = computed(() => '150px')

const getArrayFromResponse = (res: any) => {
  if (Array.isArray(res?.response?.data)) return res.response.data
  if (Array.isArray(res?.response)) return res.response
  if (Array.isArray(res?.response?.children)) return res.response.children
  if (res?.response && typeof res.response === 'object') return [res.response]
  if (Array.isArray(res?.data)) return res.data
  return []
}

const loadRoleList = async () => {
  try {
    loading.value = true
    const res: any = await PermissionManagement.getAllRole()
    if (res && res.status === 200 && res.success) {
      roleList.value = normalizeRoleList(getArrayFromResponse(res))
      const maxPage = Math.max(1, Math.ceil(filteredRoleList.value.length / pagination.pageSize))
      if (pagination.page > maxPage) {
        pagination.page = 1
      }
      return
    }
    roleList.value = []
    ElMessage.error(res?.msg || '加载角色列表失败')
  } catch (error) {
    console.error('加载角色列表失败:', error)
    roleList.value = []
    ElMessage.error('加载角色列表失败')
  } finally {
    loading.value = false
  }
}

const handleKeywordSearch = () => {
  activeKeyword.value = keyword.value
  pagination.page = 1
}

const handlePageChange = (page: number) => {
  pagination.page = page
}

const handlePageSizeChange = (size: number) => {
  pagination.pageSize = size
  pagination.page = 1
}

const getTableIndex = (index: number) => {
  return (pagination.page - 1) * pagination.pageSize + index + 1
}

const loadDepartmentTree = async () => {
  try {
    departmentTreeLoading.value = true
    const res: any = await PermissionManagement.getDepartmentTree({ pid: '0' })
    if (res && res.status === 200 && res.success) {
      const source = Array.isArray(res?.response?.children)
        ? res.response.children
        : Array.isArray(res?.response)
          ? res.response
          : res?.response
            ? [res.response]
            : []
      departmentTree.value = normalizeDepartmentTree(source)
      await applyDepartmentCheckedState()
      return
    }
    departmentTree.value = []
    ElMessage.error(res?.msg || '加载部门树失败')
  } catch (error) {
    console.error('加载部门树失败:', error)
    departmentTree.value = []
    ElMessage.error('加载部门树失败')
  } finally {
    departmentTreeLoading.value = false
  }
}

const loadPermissionTree = async () => {
  try {
    treeLoading.value = true
    const res: any = await PermissionManagement.getPermissionTree()
    if (res && res.status === 200 && res.success) {
      const treeSource = getArrayFromResponse(res)
      permissionTree.value = normalizePermissionTree(treeSource)
      return
    }
    permissionTree.value = []
    ElMessage.error(res?.msg || '加载菜单权限树失败')
  } catch (error) {
    console.error('加载菜单权限树失败:', error)
    permissionTree.value = []
    ElMessage.error('加载菜单权限树失败')
  } finally {
    treeLoading.value = false
  }
}

const extractPermissionIds = (res: any): string[] => {
  const source = res?.response?.data ?? res?.response
  if (Array.isArray(source)) {
    return source
      .map((id: any) => extractIdFromItem(id))
      .filter((id: string | null): id is string => Boolean(id))
  }
  if (source && Array.isArray(source.permissionids)) {
    const menuIds = source.permissionids
      .map((id: any) => extractIdFromItem(id))
      .filter((id: string | null): id is string => Boolean(id))
    const btnIds = Array.isArray(source.assignbtns)
      ? source.assignbtns
          .map((id: any) => extractIdFromItem(id))
          .filter((id: string | null): id is string => Boolean(id))
      : []
    return Array.from(new Set([...menuIds, ...btnIds]))
  }
  if (source && Array.isArray(source.pids)) {
    return source.pids
      .map((id: any) => extractIdFromItem(id))
      .filter((id: string | null): id is string => Boolean(id))
  }
  if (source && Array.isArray(source.PermissionIds)) {
    const menuIds = source.PermissionIds
      .map((id: any) => extractIdFromItem(id))
      .filter((id: string | null): id is string => Boolean(id))
    const btnIds = Array.isArray(source.AssignBtns)
      ? source.AssignBtns
          .map((id: any) => extractIdFromItem(id))
          .filter((id: string | null): id is string => Boolean(id))
      : []
    return Array.from(new Set([...menuIds, ...btnIds]))
  }
  if (typeof source === 'string') {
    return source
      .split(',')
      .map((id: string) => id.trim())
      .filter((id: string) => Boolean(id))
  }
  return []
}

const loadRolePermissions = async (roleId: string) => {
  try {
    const res: any = await PermissionManagement.getPermissionIdByRoleId({ rid: roleId })
    if (res && res.status === 200 && res.success) {
      checkedPermissionIds.value = extractPermissionIds(res)
    } else {
      checkedPermissionIds.value = []
    }
  } catch (error) {
    console.error('加载角色菜单关系失败:', error)
    checkedPermissionIds.value = []
  }
}

const parseDepartmentIds = (dids: string | null | undefined) => {
  if (!dids) return []
  return dids
    .split(',')
    .map((id: string) => id.trim())
    .filter((id: string) => Boolean(id))
}

const getCheckedDepartmentKeys = () => {
  // check-strictly 模式下，仍需保存半选状态节点（后端需要）
  const checked = (departmentTreeRef.value?.getCheckedKeys(false) as Array<string | number>) || []
  const halfChecked = (departmentTreeRef.value?.getHalfCheckedKeys() as Array<string | number>) || []
  return Array.from(new Set([...checked, ...halfChecked].map(item => String(item))))
}

const collectAllDepartmentKeys = (nodes: DepartmentNode[]) => {
  const keys: string[] = []
  const walk = (list: DepartmentNode[]) => {
    list.forEach(node => {
      keys.push(node.value)
      if (Array.isArray(node.children) && node.children.length > 0) {
        walk(node.children)
      }
    })
  }
  walk(nodes)
  return keys
}

const collectLeafDepartmentKeys = (nodes: DepartmentNode[]): Set<string> => {
  const leafKeys = new Set<string>()
  const walk = (list: DepartmentNode[]) => {
    list.forEach(node => {
      if (!node.children || node.children.length === 0) {
        leafKeys.add(node.value)
      } else {
        walk(node.children)
      }
    })
  }
  walk(nodes)
  return leafKeys
}

const applyDepartmentCheckedState = async () => {
  await nextTick()
  if (!departmentTree.value.length || !selectedDepartmentIds.value.length) {
    departmentTreeRef.value?.setCheckedKeys([])
    return
  }
  
  const allKeySet = new Set(collectAllDepartmentKeys(departmentTree.value))
  const leafKeySet = collectLeafDepartmentKeys(departmentTree.value)
  
  // 只设置叶子节点，避免父节点ID导致所有子节点被勾选
  const checkedLeafKeys = selectedDepartmentIds.value.filter(id => 
    allKeySet.has(id) && leafKeySet.has(id)
  )
  
  departmentTreeRef.value?.setCheckedKeys(checkedLeafKeys)
}

const setAllDepartmentChecked = () => {
  const allKeys = collectAllDepartmentKeys(departmentTree.value)
  selectedDepartmentIds.value = allKeys
  departmentTreeRef.value?.setCheckedKeys(allKeys)
}

const clearDepartmentChecked = () => {
  selectedDepartmentIds.value = []
  departmentTreeRef.value?.setCheckedKeys([])
}

const handleDepartmentTreeCheck = () => {
  selectedDepartmentIds.value = getCheckedDepartmentKeys()
}

const collectDepartmentNodesByKeyword = (
  nodes: DepartmentNode[],
  keywordText: string,
  matches: DepartmentNode[] = []
) => {
  for (const node of nodes) {
    if (node.label.toLowerCase().includes(keywordText)) {
      matches.push(node)
    }
    if (Array.isArray(node.children) && node.children.length > 0) {
      collectDepartmentNodesByKeyword(node.children, keywordText, matches)
    }
  }
  return matches
}

const collectPermissionNodesByKeyword = (
  nodes: PermissionNode[],
  keywordText: string,
  matches: PermissionNode[] = []
) => {
  for (const node of nodes) {
    if (node.label.toLowerCase().includes(keywordText)) {
      matches.push(node)
    }
    if (Array.isArray(node.children) && node.children.length > 0) {
      collectPermissionNodesByKeyword(node.children, keywordText, matches)
    }
  }
  return matches
}

const scrollCurrentNodeInTree = async (treeRef: any, scrollbarRef: any) => {
  await nextTick()
  const treeRootEl = (treeRef?.value as any)?.$el as HTMLElement | undefined
  const scrollbarRootEl = ((scrollbarRef?.value as any)?.$el ?? scrollbarRef?.value) as HTMLElement | undefined
  const currentNodeEl = treeRootEl?.querySelector('.el-tree-node.is-current') as HTMLElement | null
  const scrollWrapEl = scrollbarRootEl?.querySelector('.el-scrollbar__wrap') as HTMLElement | null
  if (!currentNodeEl || !scrollWrapEl) return
  const nodeRect = currentNodeEl.getBoundingClientRect()
  const wrapRect = scrollWrapEl.getBoundingClientRect()
  const targetTop = scrollWrapEl.scrollTop + (nodeRect.top - wrapRect.top) - scrollWrapEl.clientHeight / 2 + nodeRect.height / 2
  scrollWrapEl.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' })
}

const handleDepartmentSearch = async () => {
  const keywordText = departmentSearchKeyword.value.trim().toLowerCase()
  if (!keywordText) {
    departmentSearchState.keyword = ''
    departmentSearchState.index = -1
    ElMessage.warning('请输入要搜索的部门名称')
    return
  }
  const matches = collectDepartmentNodesByKeyword(departmentTree.value, keywordText)
  if (departmentSearchState.keyword !== keywordText) {
    departmentSearchState.keyword = keywordText
    departmentSearchState.index = -1
  }
  const nextIndex = (departmentSearchState.index + 1) % matches.length
  const targetNode = matches[nextIndex]
  if (!targetNode) {
    departmentSearchState.index = -1
    ElMessage.warning('未找到匹配的部门')
    return
  }
  departmentSearchState.index = nextIndex
  departmentTreeRef.value?.setCurrentKey(targetNode.value)
  await scrollCurrentNodeInTree(departmentTreeRef, departmentScrollbarRef)
}

const handlePermissionSearch = async () => {
  const keywordText = permissionSearchKeyword.value.trim().toLowerCase()
  if (!keywordText) {
    permissionSearchState.keyword = ''
    permissionSearchState.index = -1
    ElMessage.warning('请输入要搜索的菜单名称')
    return
  }
  const matches = collectPermissionNodesByKeyword(permissionTree.value, keywordText)
  if (permissionSearchState.keyword !== keywordText) {
    permissionSearchState.keyword = keywordText
    permissionSearchState.index = -1
  }
  const nextIndex = (permissionSearchState.index + 1) % matches.length
  const targetNode = matches[nextIndex]
  if (!targetNode) {
    permissionSearchState.index = -1
    ElMessage.warning('未找到匹配的菜单')
    return
  }
  permissionSearchState.index = nextIndex
  permissionTreeRef.value?.setCurrentKey(targetNode.value)
  await scrollCurrentNodeInTree(permissionTreeRef, permissionScrollbarRef)
}

watch(departmentSearchKeyword, (value) => {
  const nextKeyword = value.trim().toLowerCase()
  if (nextKeyword !== departmentSearchState.keyword) {
    departmentSearchState.keyword = nextKeyword
    departmentSearchState.index = -1
  }
})

watch(permissionSearchKeyword, (value) => {
  const nextKeyword = value.trim().toLowerCase()
  if (nextKeyword !== permissionSearchState.keyword) {
    permissionSearchState.keyword = nextKeyword
    permissionSearchState.index = -1
  }
})

const resetForm = () => {
  formData.IsDeleted = false
  formData.Name = ''
  formData.Description = ''
  formData.OrderSort = 1
  formData.Dids = ''
  formData.AuthorityScope = -1
  formData.Enabled = true
  formData.CreateId = null
  formData.CreateBy = null
  formData.CreateTime = null
  formData.ModifyId = null
  formData.ModifyBy = null
  formData.ModifyTime = null
  formData.Id = null
  checkedPermissionIds.value = []
  selectedDepartmentIds.value = []
  selectedBtnIds.value = []
  departmentSearchKeyword.value = ''
  permissionSearchKeyword.value = ''
  departmentSearchState.keyword = ''
  departmentSearchState.index = -1
  permissionSearchState.keyword = ''
  permissionSearchState.index = -1
}

const fillForm = (row: RoleItem) => {
  formData.IsDeleted = row.IsDeleted
  formData.Name = row.Name
  formData.Description = row.Description || ''
  formData.OrderSort = row.OrderSort
  formData.Dids = row.Dids || ''
  formData.AuthorityScope = row.AuthorityScope
  formData.Enabled = row.Enabled
  formData.CreateId = row.CreateId
  formData.CreateBy = row.CreateBy
  formData.CreateTime = row.CreateTime
  formData.ModifyId = row.ModifyId
  formData.ModifyBy = row.ModifyBy
  formData.ModifyTime = row.ModifyTime
  formData.Id = row.Id
  selectedDepartmentIds.value = parseDepartmentIds(row.Dids)
}

const collectAllNodeKeys = (nodes: PermissionNode[]) => {
  const keys: string[] = []
  const walk = (list: PermissionNode[]) => {
    list.forEach(node => {
      keys.push(node.value)
      if (Array.isArray(node.children) && node.children.length > 0) {
        walk(node.children)
      }
    })
  }
  walk(nodes)
  return keys
}

const collectAllBtnKeys = (nodes: PermissionNode[]) => {
  const keys: string[] = []
  const walk = (list: PermissionNode[]) => {
    list.forEach(node => {
      if (Array.isArray(node.btns) && node.btns.length > 0) {
        node.btns.forEach(btn => keys.push(btn.value))
      }
      if (Array.isArray(node.children) && node.children.length > 0) {
        walk(node.children)
      }
    })
  }
  walk(nodes)
  return keys
}

const getTreeCheckedNodeKeys = () => {
  const checked = (permissionTreeRef.value?.getCheckedKeys(false) as Array<string | number>) || []
  const halfChecked = (permissionTreeRef.value?.getHalfCheckedKeys() as Array<string | number>) || []
  return Array.from(new Set([...checked, ...halfChecked].map(item => String(item))))
}

const collectLeafNodeKeys = (nodes: PermissionNode[]): Set<string> => {
  const leafKeys = new Set<string>()
  const walk = (list: PermissionNode[]) => {
    list.forEach(node => {
      if (!node.children || node.children.length === 0) {
        leafKeys.add(node.value)
      } else {
        walk(node.children)
      }
    })
  }
  walk(nodes)
  return leafKeys
}

const applyPermissionCheckedState = async () => {
  await nextTick()
  const nodeKeySet = new Set(collectAllNodeKeys(permissionTree.value))
  const leafKeySet = collectLeafNodeKeys(permissionTree.value)
  const btnKeySet = new Set(collectAllBtnKeys(permissionTree.value))
  const checkedLeafKeys: string[] = []
  const checkedBtnKeys: string[] = []

  checkedPermissionIds.value.forEach(id => {
    if (nodeKeySet.has(id) && leafKeySet.has(id)) {
      checkedLeafKeys.push(id)
    }
    if (btnKeySet.has(id)) {
      checkedBtnKeys.push(id)
    }
  })

  permissionTreeRef.value?.setCheckedKeys(checkedLeafKeys)
  selectedBtnIds.value = Array.from(new Set(checkedBtnKeys))
}

const openDialog = async (roleId?: string) => {
  dialogVisible.value = true
  await nextTick()
  if (roleId) {
    const tasks: Promise<any>[] = [loadPermissionTree(), loadRolePermissions(roleId)]
    if (isCustomAuthorityScope.value) {
      tasks.push(loadDepartmentTree())
    }
    await Promise.all(tasks)
    await applyPermissionCheckedState()
    return
  }
  const tasks: Promise<any>[] = [loadPermissionTree()]
  if (isCustomAuthorityScope.value) {
    tasks.push(loadDepartmentTree())
  }
  await Promise.all(tasks)
  await applyPermissionCheckedState()
}

const handleCreate = async () => {
  dialogMode.value = 'create'
  resetForm()
  await openDialog()
}

const handleEdit = async (row: RoleItem) => {
  dialogMode.value = 'edit'
  resetForm()
  fillForm(row)
  await openDialog(row.Id)
}

const getRolePayload = () => {
  return {
    IsDeleted: formData.IsDeleted,
    Name: formData.Name,
    Description: formData.Description || null,
    OrderSort: formData.OrderSort,
    Dids: formData.Dids || null,
    AuthorityScope: formData.AuthorityScope,
    Enabled: formData.Enabled,
    CreateId: formData.CreateId,
    CreateBy: formData.CreateBy,
    CreateTime: formData.CreateTime,
    ModifyId: formData.ModifyId,
    ModifyBy: formData.ModifyBy,
    ModifyTime: formData.ModifyTime,
    Id: formData.Id
  }
}

const getCreatedRoleId = (res: any): string | null => {
  const candidates = [
    res?.response?.Id,
    res?.response?.id,
    res?.response?.data?.Id,
    res?.response?.data?.id
  ]
  for (const item of candidates) {
    if (item !== undefined && item !== null && item !== '') {
      return String(item)
    }
  }
  return null
}

const getCheckedPermissionNumberIds = () => {
  const merged = Array.from(new Set([...getTreeCheckedNodeKeys(), ...selectedBtnIds.value]))
  return merged
    .map(item => Number(item))
    .filter(item => !Number.isNaN(item))
}

const isBtnChecked = (btnId: string) => {
  return selectedBtnIds.value.includes(String(btnId))
}

const handleBtnCheckChange = (node: PermissionNode, btn: PermissionBtn, checked: boolean) => {
  const btnId = String(btn.value)
  if (checked) {
    if (!selectedBtnIds.value.includes(btnId)) {
      selectedBtnIds.value.push(btnId)
    }
    permissionTreeRef.value?.setChecked(node.value, true, false)
    return
  }
  selectedBtnIds.value = selectedBtnIds.value.filter(id => id !== btnId)
}

const collectBtnKeysByActiveNode = (nodes: PermissionNode[], activeNodeKeySet: Set<string>) => {
  const keys: string[] = []
  const walk = (list: PermissionNode[]) => {
    list.forEach(node => {
      if (activeNodeKeySet.has(node.value) && Array.isArray(node.btns) && node.btns.length > 0) {
        node.btns.forEach(btn => keys.push(btn.value))
      }
      if (Array.isArray(node.children) && node.children.length > 0) {
        walk(node.children)
      }
    })
  }
  walk(nodes)
  return keys
}

const handleTreeCheck = (_data: PermissionNode, checkedState: any) => {
  const checkedKeys = Array.isArray(checkedState?.checkedKeys) ? checkedState.checkedKeys : []
  const halfCheckedKeys = Array.isArray(checkedState?.halfCheckedKeys) ? checkedState.halfCheckedKeys : []
  const activeNodeKeySet = new Set([...checkedKeys, ...halfCheckedKeys].map(item => String(item)))
  const allowedBtnSet = new Set(collectBtnKeysByActiveNode(permissionTree.value, activeNodeKeySet))
  selectedBtnIds.value = selectedBtnIds.value.filter(id => allowedBtnSet.has(id))
}

const assignPermissionForRole = async (roleId: string) => {
  const rid = Number(roleId)
  if (Number.isNaN(rid)) {
    throw new Error('角色ID无效，无法保存菜单权限')
  }
  const pids = getCheckedPermissionNumberIds()
  const res: any = await PermissionManagement.assignPermission({ pids, rid })
  if (!(res && res.status === 200 && res.success)) {
    throw new Error(res?.msg || '菜单权限保存失败')
  }
}

const handleSubmit = () => {
  formRef.value?.validate(async (valid: boolean) => {
    if (!valid) return

    try {
      saving.value = true
      const isCreate = dialogMode.value === 'create'
      // 自定义数据权限时，部门ID来自部门树勾选结果
      formData.Dids = isCustomAuthorityScope.value ? getCheckedDepartmentKeys().join(',') : ''
      const payload = getRolePayload()
      const roleRes: any = isCreate
        ? await PermissionManagement.createRole(payload)
        : await PermissionManagement.updateRole(payload)

      if (!(roleRes && roleRes.status === 200 && roleRes.success)) {
        ElMessage.error(roleRes?.msg || (isCreate ? '新增角色失败' : '更新角色失败'))
        return
      }

      let roleId = formData.Id
      if (isCreate) {
        roleId = getCreatedRoleId(roleRes)
        if (!roleId) {
          await loadRoleList()
          const fallback = roleList.value.find(item => item.Name === formData.Name)
          roleId = fallback?.Id || null
        }
      }

      if (!roleId) {
        ElMessage.error('角色保存成功，但未获取到角色ID，无法分配菜单权限')
        await loadRoleList()
        dialogVisible.value = false
        return
      }

      await assignPermissionForRole(roleId)
      try {
        await refreshCurrentUserMenus(router)
      } catch (refreshError) {
        console.error('刷新当前用户菜单失败:', refreshError)
      }
      ElMessage.success(isCreate ? '新增角色成功' : '更新角色成功')
      dialogVisible.value = false
      await loadRoleList()
    } catch (error: any) {
      console.error('保存角色失败:', error)
      ElMessage.error(error?.message || '保存角色失败')
    } finally {
      saving.value = false
    }
  })
}

const handleDelete = (row: RoleItem) => {
  if (!row.Id) {
    ElMessage.warning('角色ID为空，无法删除')
    return
  }

  ElMessageBox.confirm(`确定删除角色“${row.Name}”吗？`, '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      try {
        const res: any = await PermissionManagement.deleteRole({ id: row.Id })
        if (res && res.status === 200 && res.success) {
          ElMessage.success('删除成功')
          await loadRoleList()
          return
        }
        ElMessage.error(res?.msg || '删除失败')
      } catch (error) {
        console.error('删除角色失败:', error)
        ElMessage.error('删除失败')
      }
    })
    .catch(() => {
      // 用户取消删除
    })
}

const collectAllPermissionKeys = (nodes: PermissionNode[]) => {
  const keys: string[] = []
  const walk = (list: PermissionNode[]) => {
    list.forEach(node => {
      keys.push(node.value)
      if (Array.isArray(node.children) && node.children.length > 0) {
        walk(node.children)
      }
    })
  }
  walk(nodes)
  return keys
}

const setAllPermissionChecked = () => {
  const allNodeKeys = collectAllPermissionKeys(permissionTree.value)
  const allBtnKeys = collectAllBtnKeys(permissionTree.value)
  permissionTreeRef.value?.setCheckedKeys(allNodeKeys)
  selectedBtnIds.value = Array.from(new Set(allBtnKeys))
}

const clearPermissionChecked = () => {
  permissionTreeRef.value?.setCheckedKeys([])
  selectedBtnIds.value = []
}

onMounted(() => {
  loadRoleList()
  calculateTableHeight()
  window.addEventListener('resize', calculateTableHeight)
})

watch(
  () => formData.AuthorityScope,
  async (scope: number) => {
    if (!dialogVisible.value || Number(scope) !== 1) return
    if (departmentTree.value.length === 0) {
      await loadDepartmentTree()
      return
    }
    await applyDepartmentCheckedState()
  }
)

watch(dialogVisible, (visible: boolean) => {
  if (visible) {
    // 打开弹框时锁定页面整体滚动
    bodyOverflowBackup.value = document.body.style.overflow
    htmlOverflowBackup.value = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
  } else {
    // 关闭弹框后恢复页面整体滚动
    document.body.style.overflow = bodyOverflowBackup.value
    document.documentElement.style.overflow = htmlOverflowBackup.value
  }
})

onUnmounted(() => {
  document.body.style.overflow = bodyOverflowBackup.value
  document.documentElement.style.overflow = htmlOverflowBackup.value
  window.removeEventListener('resize', calculateTableHeight)
})
</script>

<style scoped lang="less">
.role-management {
  min-height: 0;

  .main-card {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    :deep(.el-card__body) {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      gap: 12px;
      overflow: hidden;
    }
  }

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    .page-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }

    .toolbar-right {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
  }

  :deep(.el-table) {
    flex: 1;
    min-height: 0;
  }

  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    padding-top: 8px;
  }
}

.permission-tree-panel {
  border: 1px solid #e6ebf5;
  border-radius: 10px;
  padding: 12px;
  background: #fcfdff;

  .permission-tree-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 10px;

    .permission-tree-title {
      font-size: 15px;
      font-weight: 600;
      color: #1f2d3d;
    }

    .permission-tree-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: flex-end;
    }
  }

  .permission-tree-scroll {
    height: 360px;
    border: 1px solid #edf1f7;
    border-radius: 8px;
    padding: 8px;
    background: #fff;
  }

  &.with-department {
    .permission-tree-scroll {
      height: 250px;
    }
  }
}

.department-tree-panel {
  border: 1px solid #e6ebf5;
  border-radius: 10px;
  padding: 12px;
  background: #fcfdff;
  margin-bottom: 12px;

  .department-tree-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 10px;

    .department-tree-title {
      font-size: 15px;
      font-weight: 600;
      color: #1f2d3d;
    }

    .department-tree-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: flex-end;
    }
  }

  .department-tree-scroll {
    height: 180px;
    border: 1px solid #edf1f7;
    border-radius: 8px;
    padding: 8px;
    background: #fff;
  }
}

:deep(.permission-tree-scroll .el-scrollbar__wrap) {
  overflow: auto;
}

:deep(.department-tree-scroll .el-scrollbar__wrap) {
  overflow: auto;
}

:deep(.department-tree-scroll .el-tree-node__content) {
  min-height: 34px;
  border-radius: 6px;
}

:deep(.department-tree-scroll .el-tree-node__content:hover) {
  background-color: #f5f9ff;
}

:deep(.permission-tree-scroll .el-tree-node) {
  white-space: normal;
}

:deep(.permission-tree-scroll .el-tree-node__content) {
  height: auto;
  min-height: 38px;
  align-items: center;
  padding: 6px 4px 6px 0;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

:deep(.permission-tree-scroll .el-tree-node__content:hover) {
  background-color: #f5f9ff;
}

:deep(.permission-tree-scroll .el-tree-node__expand-icon) {
  margin-top: 0;
}

:deep(.permission-tree-scroll .el-tree-node__content > .el-checkbox) {
  margin-top: 0;
  align-self: center;
}

.tree-node-content {
  display: grid;
  grid-template-columns: minmax(120px, 220px) minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  width: 100%;
  padding-right: 8px;
}

.tree-node-label {
  line-height: 22px;
  color: #303133;
  font-weight: 500;
  word-break: break-word;
}

.tree-node-btns {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 6px 14px;
  min-height: 24px;
  padding-top: 1px;
}

:deep(.tree-node-btns .el-checkbox) {
  margin-right: 0;
  height: 24px;
}

:deep(.tree-node-btns .el-checkbox__label) {
  padding-left: 6px;
  color: #48576a;
}

.role-form-panel {
  border: 1px solid #e6ebf5;
  border-radius: 10px;
  padding: 12px 12px 4px;
  margin-bottom: 12px;
  background: #fcfdff;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
  margin-bottom: 10px;
}

:deep(.role-edit-dialog .el-dialog) {
  height: calc(100vh - 14vh);
  max-height: calc(100vh - 14vh);
  min-height: 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 36px rgba(31, 45, 61, 0.18);
  display: flex;
  flex-direction: column;
  margin: 0 auto !important;
}

:deep(.role-edit-dialog .el-dialog__header) {
  padding: 16px 20px 14px;
  border-bottom: 1px solid #edf1f7;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

:deep(.role-edit-dialog .el-dialog__title) {
  font-size: 18px;
  font-weight: 600;
  color: #1f2d3d;
}

:deep(.role-edit-dialog .el-dialog__body) {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 20px 10px;
  background: #f7f9fc;
}

:deep(.role-edit-dialog .el-dialog__footer) {
  padding: 12px 20px 16px;
  border-top: 1px solid #edf1f7;
  background: #ffffff;
}

:deep(.role-edit-modal .el-overlay-dialog) {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 4vh 0;
  box-sizing: border-box;
  overflow: hidden;
}

@media (max-width: 768px) {
  .role-management {
    .toolbar {
      flex-direction: column;
      align-items: flex-start;

      .toolbar-right {
        width: 100%;
        flex-wrap: wrap;
      }
    }
  }

  .permission-tree-panel {
    .permission-tree-header {
      flex-direction: column;
      align-items: flex-start;

      .permission-tree-actions {
        width: 100%;
      }
    }

    .permission-tree-scroll {
      height: 240px;
    }
  }

  .department-tree-panel {
    .department-tree-scroll {
      height: 180px;
    }
  }

  .department-tree-panel {
    .department-tree-header {
      flex-direction: column;
      align-items: flex-start;

      .department-tree-actions {
        width: 100%;
      }
    }
  }

  .tree-node-content {
    grid-template-columns: minmax(0, 1fr);
    gap: 4px;
  }
}
</style>
